import type { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import stringify from 'fast-json-stable-stringify';
import { buffer, mergeMap, of, sampleTime } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { GuestActions, UserActions } from '@core/account-data';
import type { User, UserBase } from '@core/account-types';
import { CandidateActions } from '@core/candidate-data';
import type { Candidate } from '@core/candidate-types';
import { createEffectFactory, objectToScope } from '@core/ngrx-legacy';
import type { Guest } from '@core/notes-types';
import { JobsWrapperActionsService, StageActions } from '@core/offers-data';
import type { Stage } from '@core/stages-types';
import { TalentPoolsBriefActions } from '@core/talent-pools-data';
import type { TalentPoolBrief } from '@core/talent-pools-types';

import { InterviewResultsHttpClient } from './interview-results.http-client';
import { InterviewResultsActions } from './interview-results.store';
import { InterviewResultsHiddenPreviewActions } from '../hidden-results-preview/hidden-results-preview.store';
import type { InterviewResultsReferences } from '../types';

@Injectable({ providedIn: 'root' })
export class InterviewResultsEffects {
  private readonly jobsWrapperActionsService = inject(JobsWrapperActionsService);
  private readonly actions$ = inject(Actions);
  private readonly http = inject(InterviewResultsHttpClient);

  public effects = createEffectFactory(InterviewResultsActions, this.actions$);

  public fetchCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewResultsActions.fetchCollection),
      switchMap(({ payload }) =>
        this.http.fetchCollection(payload!.params!).pipe(
          switchMap(({ requests, meta, hiddenResultsPreview, references }) => [
            ...this.getReferencesActions(references),
            InterviewResultsActions.fetchCollectionSuccess({
              payload: {
                data: requests,
                meta,
                scope: objectToScope(payload.params),
                replace: payload.replace!,
                references,
              },
            }),
            InterviewResultsHiddenPreviewActions.fetchCollection({
              payload: {
                data: hiddenResultsPreview,
                references,
                scope: objectToScope(payload.params),
                replace: payload.replace!,
              },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(InterviewResultsActions.fetchCollectionFailed({ payload: { error } })),
          ),
        ),
      ),
    ),
  );

  public fetchCollectionBuffered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewResultsActions.fetchCollectionBuffered),
      buffer(
        this.actions$.pipe(
          ofType(InterviewResultsActions.fetchCollectionBuffered),
          sampleTime(100),
        ),
      ),
      mergeMap(actions =>
        [...new Set(actions.map(({ payload }) => stringify(payload.params)))].map(json =>
          JSON.parse(json),
        ),
      ),
      mergeMap(fetchParams =>
        this.http.fetchCollection(fetchParams).pipe(
          switchMap(({ requests, hiddenResultsPreview, meta, references }) => [
            ...this.getReferencesActions(references),
            InterviewResultsActions.fetchCollectionBufferedSuccess({
              payload: { data: requests, meta, scope: objectToScope(fetchParams), references },
            }),
            InterviewResultsHiddenPreviewActions.fetchCollection({
              payload: {
                data: hiddenResultsPreview,
                references,
                scope: objectToScope(fetchParams),
              },
            }),
          ]),
          catchError(error =>
            of(
              InterviewResultsActions.fetchCollectionBufferedFailed({
                payload: { error, scope: objectToScope(fetchParams) },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public fetchItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewResultsActions.fetchItem),
      switchMap(({ payload }) =>
        this.http.fetchItem(payload.id).pipe(
          switchMap(data => [
            ...this.getReferencesActions(data.references),
            InterviewResultsActions.fetchItemSuccess({
              payload: {
                data: {
                  interviewResult: data.interviewResult,
                },
              },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(InterviewResultsActions.fetchItemFailed({ payload: { error } })),
          ),
        ),
      ),
    ),
  );

  public create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewResultsActions.createOnePessimistic),
      switchMap(({ payload }) =>
        this.http.create(payload.data.value, payload.data?.candidateId).pipe(
          switchMap(data => [
            ...this.getReferencesActions(data.references),
            InterviewResultsActions.createOnePessimisticSuccess({
              payload: {
                data: {
                  ...data.interviewResult,
                },
                scope: payload.scope,
                correlationId: payload.correlationId,
              },
            }),
          ]),
          catchError(error => [
            InterviewResultsActions.createOnePessimisticFailed({ payload: error }),
          ]),
        ),
      ),
    ),
  );

  public update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewResultsActions.update),
      switchMap(({ payload }) =>
        this.http.update(payload.data.id, payload.data.value).pipe(
          switchMap(data => [
            ...this.getReferencesActions(data.references),
            InterviewResultsActions.updateSuccess({
              payload: {
                data: data.interviewResult,
                scope: payload.scope,
              },
            }),
          ]),
          catchError(error => [InterviewResultsActions.updateFailed({ payload: error })]),
        ),
      ),
    ),
  );

  public delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewResultsActions.deleteOnePessimistic),
      switchMap(({ payload }) =>
        this.http.delete(payload.data.id).pipe(
          switchMap(({ interviewResult }) => [
            InterviewResultsActions.deleteOnePessimisticSuccess({
              payload: {
                data: interviewResult,
                scope: payload.scope,
                correlationId: payload.correlationId,
              },
            }),
          ]),
          catchError(error => [
            InterviewResultsActions.deleteOnePessimisticFailed({ payload: error }),
          ]),
        ),
      ),
    ),
  );

  private getReferencesActions(references: InterviewResultsReferences[]) {
    const guests = <Guest[]>references.filter(({ type }) => type === 'Guest');
    const users = <UserBase[]>references.filter(({ type, id }) => type === 'Admin');
    const candidates = references.filter(({ type, id }) => type === 'Candidate');
    const offers = references.filter(({ type, id }) => type === 'Offer');
    const talentPools = references.filter(({ type, id }) => type === 'TalentPool');
    const stages = <Stage[]>references.filter(({ type, id }) => type === 'Stage');

    return [
      ...(guests.length ? [GuestActions.references({ payload: { data: guests } })] : []),
      ...(users.length ? [UserActions.references({ payload: { data: <User[]>users } })] : []),
      ...(candidates.length
        ? [CandidateActions.references({ payload: { data: <Candidate[]>candidates } })]
        : []),
      ...(offers.length
        ? [
            this.jobsWrapperActionsService.briefReferenceAction({
              payload: { data: <any>offers },
            }),
          ]
        : []),
      ...(stages.length ? [StageActions.references({ payload: { data: <Stage[]>stages } })] : []),
      ...(talentPools.length
        ? [
            TalentPoolsBriefActions.references({
              payload: { data: <TalentPoolBrief[]>talentPools },
            }),
          ]
        : []),
    ];
  }
}
